// Core
import React, {
  useCallback,
  useState,
} from 'react';

// Hooks
import { useBreakpoint } from '../../../../hooks/useBreakpoint';

// Plugins
import {
  lastOperationalEdgeStatus,
  lastOperationalUnitDataVSI,
} from '../../../../plugins/crusher';

// Components
import { Col } from '../../../../layout/elements/Col';
import { Row } from '../../../../layout/elements/Row';
import { Alert, AlertType } from '../../../../layout/elements/Alert';
import { AlertLine } from '../../../../layout/icons';
import { CameraFeed, LiveStatus } from '../../../../components/CameraFeed';

// Types
import { EquipmentUnitDataVSICrusher } from '../../../../types/crusher';
import {
  CrusherDrawerType,
  EdgeDeviceOperationalStatus,
  EdgeDeviceOperationalStatusStatus,
} from '../../../../types/global';

// Modules
import { BinFeeder } from './Modules/BinFeeder';
import { Camera } from './Modules/Camera';
import { Details } from './Modules/Details';
import { FeederInterlock } from './Modules/FeederInterlock';
import { Indicators } from './Modules/Indicators/Indicators';
import { LidStatus } from './Modules/LidStatus';
import { Lubrication } from './Modules/Lubrication';
import { MetalDetector } from './Modules/MetalDetector';
import { MotorControl } from './Modules/MotorControl';
import { MotorStatus } from './Modules/MotorStatus';
import { Vibration } from './Modules/Vibration';

// Drawers
import { Drawers, VSIDrawerType } from './Drawers';

// Maintenance
import { Maintenance } from './Maintenance';

// Trendings
import { Trendings } from './Trendings';

// View
import {
  AlertContainer,
  BinFeederAndVibrationContainer,
  LeftMetricsContainer,
  MotorStatusContainer,
  RightMetricsContainer,
  VSIMonitoringContainer,
} from './VSI.style';
import { VSIProps } from './VSI.types';

function VSI({
  alertText,
  equipmentUnitCrusher,
  operationalData,
  site,
  unitCoordinates,
  testId,
  lastLidOpenedAt,
}: VSIProps): JSX.Element {
  // Responsive conditions
  const {
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
    isXxl,
  } = useBreakpoint();
  const isMobileOrTablet: boolean = isXs || isSm || isMd || isLg;
  const isDesktop: boolean = isXl || isXxl;

  /* ***********************************************************************************************
  **************************************** INITIAL STATE *******************************************
  *********************************************************************************************** */

  type State = {
    selectedDrawer: VSIDrawerType | null;
    isDrawerOpen: boolean;
    isMaintenanceOpen: boolean;
    trendings: {
      isOpen: boolean;
    };
    cameraModal: {
      isOpen: boolean;
    };
  };

  const initialState: State = {
    selectedDrawer: null,
    isDrawerOpen: false,
    isMaintenanceOpen: false,
    trendings: {
      isOpen: false,
    },
    cameraModal: {
      isOpen: false,
    },
  };

  const [state, setState] = useState<State>(initialState);

  /* ***********************************************************************************************
  ************************************** COMPONENT HANDLING ****************************************
  *********************************************************************************************** */

  const lastUnitData: EquipmentUnitDataVSICrusher | null = lastOperationalUnitDataVSI(
    operationalData,
  );
  const lastEdgeStatus: EdgeDeviceOperationalStatus | null = lastOperationalEdgeStatus(
    operationalData,
  );

  /**
   * Handles the enabled/disabled motor status based on the enabled sensors.
   * @returns {number} - The number of columns to occupy.
   */
  const calculateMotorStatusColSpan = useCallback((): number => {
    if (!lastUnitData?.vibrationSensor1Enable && !lastUnitData?.feedSuperiorEnable) {
      return 24;
    } if (!lastUnitData?.vibrationSensor1Enable && lastUnitData?.feedSuperiorEnable) {
      return 20;
    } if (lastUnitData?.vibrationSensor1Enable && !lastUnitData?.feedSuperiorEnable) {
      return 20;
    }
    return 16;
  }, [lastUnitData]);

  /**
   * Opens a drawer with the specified type
   */
  const openDrawer = (drawerType: VSIDrawerType): void => {
    setState({
      ...state,
      selectedDrawer: drawerType,
      isDrawerOpen: true,
      isMaintenanceOpen: false,
    });
  };

  /**
   * Closes the currently open drawer
   */
  const closeDrawer = (): void => {
    setState({
      ...state,
      selectedDrawer: null,
      isDrawerOpen: false,
      isMaintenanceOpen: false,
    });
  };

  return (
    <VSIMonitoringContainer data-testid={testId}>
      {
        alertText && (
          <Row>
            <Col>
              <AlertContainer>
                <Alert
                  icon={<AlertLine />}
                  testId="alert-status"
                  title={alertText}
                  className="drawer-header-alert"
                  closable={false}
                  type={AlertType.warning}
                />
              </AlertContainer>
            </Col>
          </Row>
        )
      }

      <Row
        style={{ flex: 1, rowGap: 8 }}
        gutter={{
          xs: 0,
          sm: 0,
          md: 0,
          lg: 0,
          xl: 4,
          xxl: 4,
        }}
      >

        {/* Left Metrics Container */}
        <Col xl={5} xs={24}>
          <LeftMetricsContainer>
            {/* Details */}
            <Row style={{ flex: 1 }}>
              <Col>
                <Details
                  unitData={lastUnitData}
                  edgeStatus={lastEdgeStatus}
                  equipmentUnitCrusher={equipmentUnitCrusher}
                  testId="details-module"
                  onDrawerOpen={(drawerType: CrusherDrawerType): void => {
                    if (drawerType === CrusherDrawerType.maintenance) {
                      setState({
                        ...state,
                        isMaintenanceOpen: true,
                      });
                    }
                    if (drawerType === CrusherDrawerType.trending) {
                      setState({
                        ...state,
                        trendings: {
                          ...state.trendings,
                          isOpen: true,
                        },
                      });
                    }
                  }}
                  onModalOpen={(): void => setState((currentState: State): State => ({
                    ...currentState,
                    cameraModal: { isOpen: true },
                  }))}
                />
              </Col>
            </Row>

            {/* Indicators rendered here only on mobile/tablet */}
            {isMobileOrTablet && (
              <Indicators
                testId="indicators-module-mobile"
                unitData={lastUnitData}
                edgeStatus={lastEdgeStatus}
              />
            )}

            {/* Metal Detector */}
            {lastUnitData?.metalDetectorEnable && (
              <Row>
                <Col>
                  <MetalDetector
                    unitData={lastUnitData}
                    edgeStatus={lastEdgeStatus}
                    onOpenDrawer={() => {}} // @TODO: implement soon
                    testId="metal-detector-module"
                  />
                </Col>
              </Row>
            )}

            {/* Lid Status */}
            <Row>
              <Col>
                <LidStatus
                  unitData={lastUnitData}
                  edgeStatus={lastEdgeStatus}
                  lastLidOpenedAt={lastLidOpenedAt}
                  testId="lid-status-module"
                  onOpenDrawer={() => openDrawer(VSIDrawerType.lidStatus)}
                />
              </Col>
            </Row>

            {/* Feeder Interlock */}
            {lastUnitData?.feedSuperiorEnable && (
              <Row>
                <Col>
                  <FeederInterlock
                    onOpenDrawer={() => openDrawer(VSIDrawerType.feeder)}
                    testId="feeder-interlock-module"
                  />
                </Col>
              </Row>
            )}

            {/* Motor Control */}
            <Row>
              <Col>
                <MotorControl
                  onOpenDrawer={() => openDrawer(VSIDrawerType.motorControl)}
                  testId="motor-control-module"
                />
              </Col>
            </Row>
          </LeftMetricsContainer>
        </Col>

        {/* Right Metrics Container */}
        <Col xl={19} xs={24}>
          <RightMetricsContainer>
            {/* Indicators only on desktop */}
            {!isMobileOrTablet && (
              <Row gutter={8}>
                <Col>
                  <Indicators
                    unitData={lastUnitData}
                    edgeStatus={lastEdgeStatus}
                    testId="indicators-module"
                  />
                </Col>
              </Row>
            )}

            {/* BinFeeder + MotorStatus + Vibration with responsive layout */}
            {isDesktop && (
              <Row
                gutter={8}
                style={{ flex: 1, rowGap: 8 }}
              >
                {lastUnitData?.feedSuperiorEnable && (
                  <Col xl={4}>
                    <BinFeeder
                      unitData={lastUnitData}
                      edgeStatus={lastEdgeStatus}
                      onOpenDrawer={() => openDrawer(VSIDrawerType.feeder)}
                      testId="bin-feeder-module"
                    />
                  </Col>
                )}

                <Col xl={calculateMotorStatusColSpan()}>
                  <MotorStatus
                    unitData={lastUnitData}
                    edgeStatus={lastEdgeStatus}
                    onOpenDrawer={() => openDrawer(VSIDrawerType.motorStatus)}
                    testId="motor-status-module"
                  />
                </Col>

                {lastUnitData?.vibrationSensor1Enable && (
                  <Col xl={4}>
                    <Vibration
                      unitData={lastUnitData}
                      edgeStatus={lastEdgeStatus}
                      onOpenDrawer={() => openDrawer(VSIDrawerType.motorStatus)}
                    />
                  </Col>
                )}
              </Row>
            )}

            {isMobileOrTablet && (
              <>
                {(lastUnitData?.feedSuperiorEnable || lastUnitData?.vibrationSensor1Enable) && (
                  <BinFeederAndVibrationContainer>
                    {lastUnitData?.feedSuperiorEnable && (
                      <Col xs={lastUnitData?.vibrationSensor1Enable ? 12 : 24}>
                        <BinFeeder
                          unitData={lastUnitData}
                          edgeStatus={lastEdgeStatus}
                          onOpenDrawer={() => openDrawer(VSIDrawerType.feeder)}
                          testId="bin-feeder-module"
                        />
                      </Col>
                    )}
                    {lastUnitData?.vibrationSensor1Enable && (
                      <Col xs={lastUnitData?.feedSuperiorEnable ? 12 : 24}>
                        <Vibration
                          unitData={lastUnitData}
                          edgeStatus={lastEdgeStatus}
                          onOpenDrawer={() => openDrawer(VSIDrawerType.motorStatus)}
                        />
                      </Col>
                    )}
                  </BinFeederAndVibrationContainer>
                )}
                <MotorStatusContainer>
                  <Col xs={24}>
                    <MotorStatus
                      unitData={lastUnitData}
                      edgeStatus={lastEdgeStatus}
                      onOpenDrawer={() => openDrawer(VSIDrawerType.motorStatus)}
                      testId="motor-status-module"
                    />
                  </Col>
                </MotorStatusContainer>
              </>
            )}

            {/* Lubrication + Camera */}
            <Row
              style={{ flex: 1 }}
              gutter={{
                xs: 0,
                sm: 0,
                md: 0,
                lg: 0,
                xl: 8,
                xxl: 8,
              }}
            >
              <Col xl={12} xs={24}>
                <Lubrication
                  unitData={lastUnitData}
                  edgeStatus={lastEdgeStatus}
                  onOpenDrawer={() => openDrawer(VSIDrawerType.lubricationUnit)}
                  testId="lubrication-module"
                />
              </Col>

              {/* Camera inline only on desktop */}
              <Col xl={12} xs={0}>
                <Camera
                  title={`${site?.name}`}
                  running={lastEdgeStatus?.edgeStatus === EdgeDeviceOperationalStatusStatus.on}
                  testId="camera-module"
                  unitCoordinates={unitCoordinates}
                  gpsEnabled={equipmentUnitCrusher.gpsEnabled}
                  isOpen={!isMobileOrTablet && state.cameraModal.isOpen}
                  onOpen={(isOpen: boolean): void => setState({
                    ...state,
                    cameraModal: { isOpen },
                  })}
                />
              </Col>
            </Row>
          </RightMetricsContainer>
        </Col>
      </Row>

      {/* CameraFeed modal for mobile/tablet */}
      {isMobileOrTablet && state.cameraModal.isOpen && (
        <CameraFeed
          title={`${site?.name}`}
          isOpen={state.cameraModal.isOpen}
          live={
            lastEdgeStatus?.edgeStatus === EdgeDeviceOperationalStatusStatus.on
              ? LiveStatus.on
              : LiveStatus.off
          }
          testId="camera-feed-modal"
          unitCoordinates={unitCoordinates}
          gpsEnabled={equipmentUnitCrusher.gpsEnabled}
          onOpen={(isOpen: boolean): void => setState({
            ...state,
            cameraModal: { isOpen },
          })}
        />
      )}
      <Drawers
        selectedDrawer={state.selectedDrawer}
        isDrawerOpen={state.isDrawerOpen}
        onCloseDrawer={closeDrawer}
        unitData={lastUnitData}
        edgeStatus={lastEdgeStatus}
        equipmentUnitId={equipmentUnitCrusher.id}
        lastLidOpenedAt={lastLidOpenedAt}
        alertText={alertText}
        testId="vsi-drawers"
      />
      <Maintenance
        unitData={lastUnitData}
        isOpen={state.isMaintenanceOpen}
        edgeStatus={lastEdgeStatus}
        onClose={(): void => setState({
          ...state,
          isMaintenanceOpen: false,
        })}
      />
      {state.trendings.isOpen && (
        <Trendings
          testId="vsi-trendings"
          showTrendings={state.trendings.isOpen}
          operationalData={operationalData}
          onCloseTrendings={(): void => setState({
            ...state,
            trendings: { ...state.trendings, isOpen: false },
          })}
          idEquipmentUnit={equipmentUnitCrusher.id}
        />
      )}
    </VSIMonitoringContainer>
  );
}

export { VSI };
